import styled, { css } from "styled-components";

import { DATA_E2E_KEY, IStyledAttributes } from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greenMui,
  greyMui,
  MOBILE_BREAKPOINT,
  redMui,
  rydeColors,
  ScreenDepth,
} from "@ryde/theme";

import {
  STATION_ROUTE_ROW_GRID_GAP,
  STATION_ROUTE_ROW_GRID_TEMPLATE,
  STATION_ROUTE_STATION_BOX_LEFT_WIDTH_PX,
  STATION_ROUTE_STATION_BOX_RIGHT_WIDTH_PX,
} from "./station-route-stations-panel.constants";

export const StationRouteStationsPanelStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "station-route-station-panel",
  },
)<{
  $isDragging?: boolean;
}>`
  position: relative;
  z-index: ${ScreenDepth.MAIN_CONTAINER};
  background-color: ${rydeColors.white};
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding-bottom: 10px;
  box-sizing: border-box;
  ${({ $isDragging }) =>
    $isDragging
      ? css`
          user-select: none;
          pointer-events: none;
        `
      : css`
          overflow-x: hidden;
          overflow-y: auto;
        `};
`;

export const StationRouteStationsHeaderStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "stationRoute-station-header",
  },
)<{ $isDragging?: boolean }>`
  display: grid;
  grid-template-columns: ${STATION_ROUTE_ROW_GRID_TEMPLATE};
  column-gap: ${STATION_ROUTE_ROW_GRID_GAP};
  align-items: center;
  height: 32px;
  font-size: 12px;
  line-height: 16px;
  margin-bottom: 7px;
  border: 1px solid ${greyMui._300};
  background-color: ${greyMui._100};
  border-radius: 4px;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  color: ${({ $isDragging }) => ($isDragging ? redMui._900 : blueGreyMui._800)};
  ${({ $isDragging }) =>
    $isDragging &&
    css`
      user-select: none;
      pointer-events: none;
    `};

  .column_station_info_h {
    min-width: 0;
    display: flex;
    align-items: center;
    overflow: hidden;
  }

  .column_station_header_spacer {
    flex: 0 0 ${STATION_ROUTE_STATION_BOX_LEFT_WIDTH_PX}px;
  }

  .column_station_header {
    flex: 1 1 0;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .column_time_header {
    flex: 0 0 ${STATION_ROUTE_STATION_BOX_RIGHT_WIDTH_PX}px;
    min-width: 0;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .column_area_header {
    min-width: 0;
    padding-inline-start: 15px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .column_duration_header {
    padding-inline-start: 10px;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .column_icons_header {
    min-width: 0;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    gap: ${STATION_ROUTE_ROW_GRID_GAP};

    .column_station_info_h {
      flex: 1 1 0;
      min-width: 0;
    }
  }
`;

export const StationRouteStationStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "station-route-station",
})<{
  $isDragging?: boolean;
  $isDragClone?: boolean;
  $isWaypoint?: boolean;
}>`
  padding-top: 4px;
  margin-bottom: 3px;
  border-bottom: 1px solid ${greyMui._300};
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background-color: ${({ $isWaypoint }) =>
    $isWaypoint ? rydeColors.smoke : rydeColors.white};
  ${({ $isDragClone }) =>
    $isDragClone &&
    css`
      cursor: pointer;
      user-select: none;
      z-index: ${ScreenDepth.RYDE_STATION_DRAG_CLONE};
      border: 3px solid ${greenMui._500};
      box-shadow: 0 2px 15px rgba(0, 0, 0, 0.25);
    `};

  ${({ $isDragging }) =>
    $isDragging &&
    css`
      cursor: pointer;
      pointer-events: none;
      user-select: none;
      opacity: 0.3;
    `};
`;

export const StationRouteStationRowStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "stationRoute-station-row",
  },
)`
  position: relative;
  z-index: ${ScreenDepth.MAIN_CONTAINER};
  display: grid;
  grid-template-columns: ${STATION_ROUTE_ROW_GRID_TEMPLATE};
  column-gap: ${STATION_ROUTE_ROW_GRID_GAP};
  align-items: center;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  height: 32px;
  font-size: 12px;
  line-height: 16px;
  color: ${rydeColors.black};
  margin-bottom: 8px;

  .column_grip {
    box-sizing: border-box;
    padding-inline-start: 9px;
    display: flex;
    align-items: center;
    &:hover {
      cursor: pointer;
    }
  }

  .column_station_info {
    min-width: 0;
    display: flex;
    overflow: hidden;
  }
  .column_area {
    color: ${blueGreyMui._500};
    box-sizing: border-box;
    padding-inline-start: 15px;
    min-width: 0;
    overflow: hidden;
  }

  .column_duration {
    padding-inline-start: 10px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  .column_icons {
    margin-inline-end: 3px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: flex;
    gap: ${STATION_ROUTE_ROW_GRID_GAP};

    .column_station_info {
      flex: 1 1 0;
      min-width: 0;
    }
  }
`;

export const StationHeaderRightIconsWrapperStyled = styled.div.attrs<IStyledAttributes>(
  {
    [DATA_E2E_KEY]: "station-header-right-icons-wrapper",
  },
)`
  display: flex;
  align-items: center;
  gap: 10px;
  justify-content: flex-end;
`;

export const StationBoxLeftStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "stationRoute-station-box-left",
})`
  border: 1px solid ${blueGreyMui.A200};
  height: 32px;
  width: 30px;
  flex: 0 0 30px;
  border-inline-end: 1px solid ${blueGreyMui.A200};
  border-radius: 4px 0 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: ${rydeColors.black};
`;

export const StationBoxCenterStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "stationRoute-station-box-center",
})`
  border-top: 1px solid ${blueGreyMui.A200};
  border-bottom: 1px solid ${blueGreyMui.A200};
  height: 32px;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-inline: 8px;
  box-sizing: border-box;
  font-weight: 400;
  font-size: 14px;
  color: ${rydeColors.black};
  overflow: hidden;
  .cell_simple {
    width: 100%;
    min-width: 0;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-align: start;
  }
`;

export const StationBoxRightStyled = styled.div.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "station-route-station-box-right",
})`
  background: ${rydeColors.blueTimeEditBg};
  border: 1px solid ${blueMui._300};
  border-radius: 0 4px 4px 0;
  height: 32px;
  width: 81px;
  flex: 0 0 81px;
  font-weight: 400;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${rydeColors.black};
`;
